import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { RootState } from "../store";
import { FrontendUser, LogType } from "../types";
import { api } from "../utils/utils";
import { Colors } from "../Colors";
import SignoutHeader from "../subcomponents/SignoutHeader";

// ---------- Design tokens ----------
const surfaces = {
  card: "rgba(255, 255, 255, 0.02)",
  cardHover: "rgba(255, 255, 255, 0.04)",
  border: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.14)",
  textPrimary: "#FFFFFF",
  textSecondary: "rgba(255, 255, 255, 0.66)",
  textMuted: "rgba(255, 255, 255, 0.42)",
  controlRadius: "10px"
};

const series = {
  bookings: "#5E78EE",
  texts: "#48A999",
  replies: "#FCD554",
  errors: "#EE5151"
};

// ---------- Assumptions for value calculations ----------
const AVG_APPOINTMENT_REVENUE_USD = 100;
// Industry-typical PT late-cancel fee. Used as an estimate because the log
// item doesn't carry the actual amount charged.
const AVG_LATE_CANCEL_FEE_USD = 50;
// Each outbound text would have taken a staff member ~2 minutes to send
// manually (look up the patient, check availability, compose, send, log).
const MINUTES_SAVED_PER_OUTBOUND_TEXT = 2;

// ---------- Classification helpers (used for activity-breakdown coloring) ----------
const BOOKING_TYPES: LogType[] = [
  LogType.appointmentBooked,
  LogType.initialVisitVisitBooked,
  LogType.planOfCareVisitBooked,
  LogType.planOfCareScheduled
];

const OUTBOUND_TYPES: LogType[] = [
  LogType.textSent,
  LogType.initialVisitLinkSent,
  LogType.planOfCareSent
];

const INBOUND_TYPES: LogType[] = [LogType.textReceived];

const ERROR_TYPES: LogType[] = [
  LogType.bookingFailed,
  LogType.aiError,
  LogType.textNotDelivered
];

// ---------- Number formatting ----------
const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
};

const formatUsd = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
};

const formatHours = (minutes: number): string => {
  const hours = minutes / 60;
  if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k hrs`;
  if (hours >= 100) return `${Math.round(hours)} hrs`;
  if (hours >= 10) return `${hours.toFixed(1)} hrs`;
  return `${hours.toFixed(1)} hrs`;
};

const formatPct = (n: number): string => `${Math.round(n * 100)}%`;

// ---------- Reporting payload shape (matches GET /api/log-items/reporting) ----------
type ReportingDailyBucket = {
  date: string; // yyyy-LL-dd, UTC
  bookings: number;
  texts: number;
  replies: number;
  errors: number;
};

type ReportingPayload = {
  range: { start: string; end: string };
  countsByType: Partial<Record<LogType, number>>;
  daily: ReportingDailyBucket[];
  uniquePatientsTexted: number;
  uniquePatientsReplied: number;
  uniquePatientsBooked: number;
  uniqueWaitlistJoined: number;
  totalItems: number;
  truncated: boolean;
};

// ==========================================================
// Stat Card
// ==========================================================
type StatCardProps = {
  label: string;
  value: string;
  sublabel?: string;
  accent?: string;
  spark?: number[];
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  sublabel,
  accent,
  spark
}) => {
  const sparkColor = accent || series.bookings;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 200,
        backgroundColor: surfaces.card,
        border: `1px solid ${surfaces.border}`,
        borderRadius: surfaces.controlRadius,
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          backgroundColor: surfaces.cardHover,
          borderColor: surfaces.borderStrong
        }
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: surfaces.textMuted
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "1.95rem",
          fontWeight: 600,
          color: surfaces.textPrimary,
          lineHeight: 1.1,
          letterSpacing: "-0.5px"
        }}
      >
        {value}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 0.5,
          minHeight: 28
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: surfaces.textSecondary
          }}
        >
          {sublabel || ""}
        </Typography>
        {spark && spark.length > 1 && (
          <Sparkline values={spark} color={sparkColor} />
        )}
      </Box>
    </Box>
  );
};

// ==========================================================
// Sparkline (mini SVG)
// ==========================================================
const Sparkline: React.FC<{ values: number[]; color: string }> = ({
  values,
  color
}) => {
  const w = 84;
  const h = 24;
  const max = Math.max(1, ...values);
  const min = Math.min(...values);
  const range = Math.max(1, max - min);
  const stepX = values.length > 1 ? w / (values.length - 1) : w;

  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  const area = `${0},${h} ${points} ${w},${h}`;

  const gradId = `spark-${color.replace("#", "")}`;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradId})`} />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

// ==========================================================
// Area Chart (main daily activity)
// ==========================================================
type ChartSeries = {
  key: string;
  label: string;
  color: string;
  values: number[];
};

const AreaChart: React.FC<{
  labels: string[];
  series: ChartSeries[];
  height?: number;
}> = ({ labels, series: seriesData, height = 260 }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(800);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 800);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const paddingLeft = 44;
  const paddingRight = 16;
  const paddingTop = 16;
  const paddingBottom = 28;

  const innerW = Math.max(50, width - paddingLeft - paddingRight);
  const innerH = height - paddingTop - paddingBottom;

  const n = labels.length;
  const maxValue = Math.max(1, ...seriesData.flatMap((s) => s.values));

  const niceMax = (() => {
    if (maxValue <= 1) return 1;
    const exp = Math.floor(Math.log10(maxValue));
    const base = Math.pow(10, exp);
    const norm = maxValue / base;
    let nice: number;
    if (norm <= 1) nice = 1;
    else if (norm <= 2) nice = 2;
    else if (norm <= 5) nice = 5;
    else nice = 10;
    return nice * base;
  })();

  const xFor = (i: number) =>
    paddingLeft + (n > 1 ? (i * innerW) / (n - 1) : innerW / 2);
  const yFor = (v: number) =>
    paddingTop + innerH - (v / niceMax) * innerH;

  const tickCount = 4;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const v = (niceMax * i) / tickCount;
    return { v, y: yFor(v) };
  });

  const xTickIndices = (() => {
    if (n <= 1) return [0];
    const desired = Math.min(6, n);
    const step = Math.max(1, Math.floor((n - 1) / (desired - 1)));
    const idxs: number[] = [];
    for (let i = 0; i < n; i += step) idxs.push(i);
    if (idxs[idxs.length - 1] !== n - 1) idxs.push(n - 1);
    return idxs;
  })();

  const buildPath = (vals: number[]): string => {
    if (vals.length === 0) return "";
    let d = "";
    vals.forEach((v, i) => {
      const x = xFor(i);
      const y = yFor(v);
      d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    });
    return d;
  };

  const buildArea = (vals: number[]): string => {
    if (vals.length === 0) return "";
    const line = buildPath(vals);
    const x0 = xFor(0);
    const xN = xFor(vals.length - 1);
    const yB = paddingTop + innerH;
    return `${line} L ${xN} ${yB} L ${x0} ${yB} Z`;
  };

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = (e.target as SVGSVGElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (n === 0) return;
    if (n === 1) {
      setHoverIdx(0);
      return;
    }
    const relative = (px - paddingLeft) / innerW;
    const idx = Math.round(relative * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  };

  return (
    <Box ref={wrapperRef} sx={{ width: "100%", position: "relative" }}>
      <svg
        width={width}
        height={height}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          {seriesData.map((s) => (
            <linearGradient
              key={s.key}
              id={`grad-${s.key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>

        {yTicks.map((t, i) => (
          <line
            key={i}
            x1={paddingLeft}
            x2={paddingLeft + innerW}
            y1={t.y}
            y2={t.y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        ))}

        {yTicks.map((t, i) => (
          <text
            key={`yl-${i}`}
            x={paddingLeft - 10}
            y={t.y + 4}
            fill={surfaces.textMuted as string}
            fontSize="11"
            textAnchor="end"
            fontFamily="inherit"
          >
            {formatCount(Math.round(t.v))}
          </text>
        ))}

        {xTickIndices.map((i) => (
          <text
            key={`xl-${i}`}
            x={xFor(i)}
            y={height - 8}
            fill={surfaces.textMuted as string}
            fontSize="11"
            textAnchor="middle"
            fontFamily="inherit"
          >
            {labels[i]}
          </text>
        ))}

        {seriesData.map((s) => (
          <path
            key={`area-${s.key}`}
            d={buildArea(s.values)}
            fill={`url(#grad-${s.key})`}
          />
        ))}

        {seriesData.map((s) => (
          <path
            key={`line-${s.key}`}
            d={buildPath(s.values)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {hoverIdx !== null && n > 0 && (
          <>
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={paddingTop}
              y2={paddingTop + innerH}
              stroke="rgba(255,255,255,0.18)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            {seriesData.map((s) => (
              <circle
                key={`dot-${s.key}`}
                cx={xFor(hoverIdx)}
                cy={yFor(s.values[hoverIdx] || 0)}
                r={3.5}
                fill={s.color}
                stroke="#0F1327"
                strokeWidth={2}
              />
            ))}
          </>
        )}
      </svg>

      {hoverIdx !== null && n > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: Math.min(
              Math.max(paddingLeft, xFor(hoverIdx) - 80),
              width - 180
            ),
            backgroundColor: "#1C2138",
            border: `1px solid ${surfaces.borderStrong}`,
            borderRadius: "8px",
            padding: "10px 12px",
            minWidth: 160,
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            pointerEvents: "none"
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: surfaces.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.75
            }}
          >
            {labels[hoverIdx]}
          </Typography>
          {seriesData.map((s) => (
            <Box
              key={`tt-${s.key}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 0.25
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    backgroundColor: s.color
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.78rem", color: surfaces.textSecondary }}
                >
                  {s.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: surfaces.textPrimary
                }}
              >
                {(s.values[hoverIdx] || 0).toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ==========================================================
// Horizontal bar row
// ==========================================================
const BarRow: React.FC<{
  label: string;
  count: number;
  total: number;
  color: string;
}> = ({ label, count, total, color }) => {
  const pct = total > 0 ? count / total : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
      <Typography
        sx={{
          fontSize: "0.82rem",
          color: surfaces.textSecondary,
          flex: "0 0 160px",
          textTransform: "capitalize"
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          sx={{
            height: 6,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.05)",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.max(2, pct * 100)}%`,
              backgroundColor: color,
              borderRadius: 999,
              transition: "width 0.4s ease"
            }}
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: "0.82rem",
          color: surfaces.textPrimary,
          fontWeight: 600,
          flex: "0 0 60px",
          textAlign: "right"
        }}
      >
        {formatCount(count)}
      </Typography>
    </Box>
  );
};

// ==========================================================
// Funnel step
// ==========================================================
const FunnelStep: React.FC<{
  label: string;
  count: number;
  baseline: number;
  color: string;
}> = ({ label, count, baseline, color }) => {
  const widthPct =
    baseline > 0 ? Math.max(8, (count / baseline) * 100) : 8;
  const conversionPct = baseline > 0 ? count / baseline : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.75
        }}
      >
        <Typography
          sx={{ fontSize: "0.85rem", color: surfaces.textSecondary }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 600,
              color: surfaces.textPrimary
            }}
          >
            {count.toLocaleString()}
          </Typography>
          <Typography
            sx={{ fontSize: "0.78rem", color: surfaces.textMuted }}
          >
            {formatPct(conversionPct)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          height: 8,
          width: "100%",
          borderRadius: 999,
          backgroundColor: "rgba(255,255,255,0.04)"
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${widthPct}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${color} 0%, ${color}aa 100%)`,
            transition: "width 0.5s ease"
          }}
        />
      </Box>
    </Box>
  );
};

// ==========================================================
// Section header & Panel
// ==========================================================
const SectionTitle: React.FC<{
  title: string;
  description?: string;
}> = ({ title, description }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: "1rem",
        fontWeight: 600,
        color: surfaces.textPrimary
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        sx={{ fontSize: "0.82rem", color: surfaces.textMuted, mt: 0.25 }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

const Panel: React.FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx
}) => (
  <Box
    sx={{
      backgroundColor: surfaces.card,
      border: `1px solid ${surfaces.border}`,
      borderRadius: surfaces.controlRadius,
      padding: "22px 24px",
      ...sx
    }}
  >
    {children}
  </Box>
);

// ==========================================================
// Main component
// ==========================================================
const Reporting: React.FC = () => {
  const user: FrontendUser | null = useSelector(
    (state: RootState) => state.auth.user
  );

  // Earliest selectable month is the org's createdAt (or 24mo ago as a
  // fallback for orgs missing that field). This stops us from offering
  // months in which the customer can't possibly have any data.
  const orgCreatedAt = useMemo(() => {
    const org = user?.organizations?.find(
      (o) => o.organizationId === user?.organizationId
    );
    if (org?.createdAt) {
      const dt = DateTime.fromISO(org.createdAt);
      if (dt.isValid) return dt.startOf("month");
    }
    return DateTime.local().minus({ months: 24 }).startOf("month");
  }, [user?.organizations, user?.organizationId]);

  const currentMonth = useMemo(
    () => DateTime.local().startOf("month"),
    []
  );

  const [selectedMonth, setSelectedMonth] = useState<DateTime>(currentMonth);

  const monthRange = useMemo(() => {
    const start = selectedMonth.startOf("month");
    const monthEnd = selectedMonth.endOf("month");
    const now = DateTime.local();
    const end = monthEnd > now ? now : monthEnd;
    return { start, end };
  }, [selectedMonth]);

  const [report, setReport] = useState<ReportingPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      if (!user?.token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          start_date: monthRange.start.toISO() || "",
          end_date: monthRange.end.toISO() || ""
        });
        const response = await api.get(
          `/log-items/reporting?${params.toString()}`,
          user.token
        );
        if (cancelled) return;
        setReport(response.data as ReportingPayload);
      } catch (e) {
        console.error("Failed to fetch reporting aggregate", e);
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [user?.token, monthRange.start.toISO(), monthRange.end.toISO()]);

  // ---------- Derived metrics from the aggregated payload ----------
  const metrics = useMemo(() => {
    const counts = (report?.countsByType || {}) as Partial<Record<LogType, number>>;

    const sumOf = (types: LogType[]) =>
      types.reduce((s, t) => s + (counts[t] || 0), 0);

    const bookingsTotal = sumOf(BOOKING_TYPES);
    const outboundTotal = sumOf(OUTBOUND_TYPES);
    const inboundTotal = sumOf(INBOUND_TYPES);
    const errorsTotal = sumOf(ERROR_TYPES);
    const cancellationsTotal = counts[LogType.appointmentCancelled] || 0;
    const lateCancelFeesCollected = counts[LogType.lateCancelFeePaid] || 0;

    const uniqueTexted = report?.uniquePatientsTexted || 0;
    const uniqueReplied = report?.uniquePatientsReplied || 0;
    const uniqueBooked = report?.uniquePatientsBooked || 0;

    const responseRate =
      uniqueTexted > 0 ? uniqueReplied / uniqueTexted : 0;
    const conversionRate =
      uniqueTexted > 0 ? uniqueBooked / uniqueTexted : 0;

    const estimatedRevenue =
      bookingsTotal * AVG_APPOINTMENT_REVENUE_USD -
      cancellationsTotal * AVG_APPOINTMENT_REVENUE_USD +
      lateCancelFeesCollected * AVG_LATE_CANCEL_FEE_USD;
    const estimatedMinutesSaved =
      outboundTotal * MINUTES_SAVED_PER_OUTBOUND_TEXT;

    return {
      counts,
      bookingsTotal,
      outboundTotal,
      inboundTotal,
      errorsTotal,
      cancellationsTotal,
      lateCancelFeesCollected,
      uniquePatientsTexted: uniqueTexted,
      uniquePatientsReplied: uniqueReplied,
      uniquePatientsBooked: uniqueBooked,
      uniqueWaitlistJoined: report?.uniqueWaitlistJoined || 0,
      responseRate,
      conversionRate,
      estimatedRevenue,
      estimatedMinutesSaved
    };
  }, [report]);

  // ---------- Daily series for the chart (built from server buckets) ----------
  const dailySeries = useMemo(() => {
    const start = monthRange.start.startOf("day");
    const end = monthRange.end.startOf("day");
    const diff = Math.max(0, Math.floor(end.diff(start, "days").days));

    const buckets: { label: string; key: string }[] = [];
    for (let i = 0; i <= diff; i++) {
      const d = start.plus({ days: i });
      buckets.push({
        label: d.toFormat("d"),
        key: d.toFormat("yyyy-LL-dd")
      });
    }

    const serverByDate = new Map(
      (report?.daily || []).map((row) => [row.date, row])
    );

    const bookings = buckets.map((b) => serverByDate.get(b.key)?.bookings || 0);
    const texts = buckets.map((b) => serverByDate.get(b.key)?.texts || 0);
    const replies = buckets.map((b) => serverByDate.get(b.key)?.replies || 0);
    const errors = buckets.map((b) => serverByDate.get(b.key)?.errors || 0);

    return {
      labels: buckets.map((b) => b.label),
      bookings,
      texts,
      replies,
      errors
    };
  }, [report, monthRange]);

  const bookingBreakdown = useMemo(() => {
    const rows: { type: LogType; label: string }[] = [
      { type: LogType.appointmentBooked, label: "Waitlist" },
      { type: LogType.initialVisitVisitBooked, label: "Initial Exam" },
      { type: LogType.planOfCareVisitBooked, label: "Plan of Care" }
    ];
    const items = rows.map(({ type, label }) => ({
      type,
      label,
      count: metrics.counts[type] || 0
    }));
    const max = items.reduce((m, i) => Math.max(m, i.count), 0);
    return { items, max };
  }, [metrics.counts]);

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: surfaces.textPrimary,
        minHeight: "100vh",
        pb: 6,
        "& .MuiOutlinedInput-root": { color: "#FFFFFF" },
        "& .MuiInputBase-input": { color: "#FFFFFF" },
        "& .MuiSvgIcon-root": { color: "#FFF" }
      }}
    >
      <SignoutHeader />

      {/* Page header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          justifyContent: "space-between",
          alignItems: "flex-end"
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
            Reporting
          </Typography>
          <Typography variant="body1" sx={{ color: Colors.info }}>
            How Penciled is filling your schedule, reaching patients, and
            saving your team time.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            minWidth: 220
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: surfaces.textMuted
            }}
          >
            Month
          </Typography>
          <DatePicker
            views={["year", "month"]}
            openTo="month"
            value={selectedMonth}
            minDate={orgCreatedAt}
            maxDate={currentMonth}
            onChange={(value) => {
              if (value && value.isValid) {
                setSelectedMonth(value.startOf("month"));
              }
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  border: `1px solid ${surfaces.border}`,
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                  "& .MuiInputBase-input": {
                    color: surfaces.textPrimary,
                    fontSize: "0.9rem",
                    padding: "8px 12px"
                  }
                }
              }
            }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 12
          }}
        >
          <CircularProgress sx={{ color: Colors.info }} size={28} />
        </Box>
      ) : (
        <>
          {/* Hero KPI cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, 1fr)"
              },
              gap: 2,
              mb: 3
            }}
          >
            <StatCard
              label="Appointments Booked"
              value={metrics.bookingsTotal.toLocaleString()}
              sublabel={`${metrics.uniquePatientsBooked.toLocaleString()} unique patients`}
              accent={series.bookings}
              spark={dailySeries.bookings}
            />
            <StatCard
              label="Revenue Captured"
              value={formatUsd(metrics.estimatedRevenue)}
              sublabel={`Est. @ $${AVG_APPOINTMENT_REVENUE_USD}/visit`}
              accent={series.bookings}
              spark={dailySeries.bookings}
            />
            <StatCard
              label="Patients Reached"
              value={metrics.uniquePatientsTexted.toLocaleString()}
              sublabel={`${metrics.outboundTotal.toLocaleString()} texts sent`}
              accent={series.texts}
              spark={dailySeries.texts}
            />
            <StatCard
              label="Team Time Saved"
              value={formatHours(metrics.estimatedMinutesSaved)}
              sublabel={`${metrics.outboundTotal.toLocaleString()} texts × ${MINUTES_SAVED_PER_OUTBOUND_TEXT} min each`}
              accent={series.replies}
              spark={dailySeries.texts}
            />
          </Box>

          {/* Main chart */}
          <Panel sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: surfaces.textPrimary
                  }}
                >
                  Activity in {selectedMonth.toFormat("LLLL yyyy")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: surfaces.textMuted,
                    mt: 0.25
                  }}
                >
                  Grouped by day
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {[
                  { label: "Bookings", color: series.bookings },
                  { label: "Texts sent", color: series.texts },
                  { label: "Replies", color: series.replies }
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "2px",
                        backgroundColor: s.color
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: surfaces.textSecondary
                      }}
                    >
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <AreaChart
              labels={dailySeries.labels}
              series={[
                {
                  key: "bookings",
                  label: "Bookings",
                  color: series.bookings,
                  values: dailySeries.bookings
                },
                {
                  key: "texts",
                  label: "Texts sent",
                  color: series.texts,
                  values: dailySeries.texts
                },
                {
                  key: "replies",
                  label: "Replies",
                  color: series.replies,
                  values: dailySeries.replies
                }
              ]}
            />
          </Panel>

          {/* Two-up: funnel + breakdown */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3
            }}
          >
            <Panel>
              <SectionTitle
                title="Patient journey"
                description="From outreach to booked appointment."
              />
              {(() => {
                const reached = metrics.uniquePatientsTexted;
                const replied = metrics.uniquePatientsReplied;
                const booked = metrics.uniquePatientsBooked;
                const baseline = Math.max(1, reached);
                return (
                  <>
                    <FunnelStep
                      label="Patients reached"
                      count={reached}
                      baseline={baseline}
                      color={series.texts}
                    />
                    <FunnelStep
                      label="Patients who replied"
                      count={replied}
                      baseline={baseline}
                      color={series.replies}
                    />
                    <FunnelStep
                      label="Patients booked"
                      count={booked}
                      baseline={baseline}
                      color={series.bookings}
                    />

                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${surfaces.border}`,
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap"
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 600,
                            color: surfaces.textMuted
                          }}
                        >
                          Reply rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.4rem",
                            fontWeight: 600,
                            color: surfaces.textPrimary
                          }}
                        >
                          {formatPct(metrics.responseRate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 600,
                            color: surfaces.textMuted
                          }}
                        >
                          Booking rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.4rem",
                            fontWeight: 600,
                            color: surfaces.textPrimary
                          }}
                        >
                          {formatPct(metrics.conversionRate)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Panel>

            <Panel>
              <SectionTitle
                title="Booking breakdown"
                description="How patients are getting on the schedule."
              />
              <Box>
                {bookingBreakdown.items.map((row) => (
                  <BarRow
                    key={row.type}
                    label={row.label}
                    count={row.count}
                    total={bookingBreakdown.max}
                    color={series.bookings}
                  />
                ))}
              </Box>
            </Panel>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Reporting;
